<template>
  <div class="population-chart" :class="className" v-inview="{ callback: onInview }">
    <h4 class="population-chart__caption">2018年人口數</h4>
    <div ref="figure" class="population-chart__figure"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import POPULATION_DATA from '@/data/population.json'

// const SVG_CONFIG = {
//   viewBox: '0 0 320 240',
//   preserveAspectRatio: 'xMidYMid meet',
//   width: 320,
//   height: 240,
//   padding: [ 10, 35, 50, 70 ]
// }

export default {
  name: 'PopulationChart',
  data () {
    return {
      isInview: false,
      data: POPULATION_DATA,
      chartWidth: 0,
      chartHeight: 0,
      svg: null,
      svgChart: null,
      svgSize: {},
      xScale: null,
      yScale: null,
      xAxis: null,
      yAxis: null,
      numericFormatter: d3.format(',')
    }
  },
  computed: {
    className () {
      return {
        'population-chart--inview': this.isInview
      }
    },
    chartData () {
      return this.data.map(row => {
        const { beginAge, endAge } = row
        const label = !endAge ? `${beginAge}以上` : `${beginAge}-${endAge}`
        return { ...row, label }
      })
    },
    axisInfo () {
      const { width } = this.svgSize
      const info = {
        titleFontSize: 12,
        tickFontSize: 12,
        tickAngle: -25,
        tickAnchor: 'end',
        padding: [ 10, 35, 50, 70 ]
      }
      if (width >= 500) {
        info.titleFontSize = 16
        info.tickFontSize = 16
        info.tickAngle = 0
        info.tickAnchor = 'middle'
        info.padding = [ 10, 35, 70, 100 ]
      }
      return { ...info }
    },
    barInfo () {
      const { width } = this.svgSize
      const info = {
        rectFill: '#c6c6c6',
        rectHighlightFill: '#e9a8a2',
        labelFill: '#727171',
        labelHighlightFill: '#231815',
        labelFontSize: 13
      }
      if (width >= 500) {
        info.labelFontSize = 16
      }
      return { ...info }
    }
  },
  methods: {
    onInview () {
      this.isInview = true
      this.$bus.$on('resize', this.resize)
      this.resize()
    },
    resize () {
      this.$nextTick(() => {
        if (!this.$refs.figure) {
          return
        }
        const bounds = this.$refs.figure.getBoundingClientRect()
        this.svgSize = {
          width: Math.round(bounds.width),
          height: Math.round(bounds.height)
        }
        this.draw()
      })
    },
    draw () {
      const { width, height } = this.svgSize
      const { titleFontSize, tickFontSize, tickAngle, tickAnchor, padding } = this.axisInfo
      // update chart size
      this.chartWidth = width - padding[1] - padding[3]
      this.chartHeight = height - padding[0] - padding[2]
      this.svgChart
        .attr('transform', `translate(${padding[3]}, ${padding[0]})`)
        .transition()
        .style('opacity', 1)
      // axis-x
      this.xScale
        .range([0, this.chartWidth])
        .domain([0, 4500000])
      this.xAxis
        .ticks(10)
        .tickFormat(d => (d / 10000))
      this.svgChart
        .select('.x-axis')
        .attr('transform', `translate(0, ${this.chartHeight})`)
        .call(this.xAxis)
        .selectAll('text')
        .classed('.axis-ruler', true)
        .attr('transform', `translate(0,0) rotate(${tickAngle})`)
        .style('text-anchor', tickAnchor)
        .style('font-size', `${tickFontSize}px`)

      // axis-y
      this.yScale
        .range([0, this.chartHeight])
        .domain(this.chartData.map(d => d.label))
        .padding(0.25)
      this.svgChart.select('.y-axis')
        .call(this.yAxis)
        .selectAll('text')
        .style('font-size', `${tickFontSize}px`)

      // render bar chart
      this.svgChart
        .selectAll('.bar')
        .data(this.chartData)
        .join(this.drawBarEnter, this.drawBarUpdate, this.drawBarExit)

      // update rwd positions
      // this.svgChart
      //   .selectAll('.bar')
      //   .attr('transform', d => `translate(${this.xScale(0) + 1}, ${this.yScale(d.label)})`)

      // this.svgChart
      //   .selectAll('.bar rect')
      //   .attr('height', this.yScale.bandwidth())
      //   .attr('width', d => this.xScale(d.population))

      // this.svgChart
      //   .selectAll('.bar text')
      //   .attr('x', d => this.xScale(d.population))
      //   .attr('y', this.yScale.bandwidth() / 2)
      //   .style('font-size', `${labelFontSize}px`)

      this.svgChart
        .select('.x-axis-title')
        .attr('x', this.chartWidth)
        .attr('y', this.chartHeight + padding[2] * 0.8)
        .style('font-size', `${titleFontSize}px`)

      this.svgChart
        .select('.y-axis-title')
        .attr('x', -10)
        .attr('y', -padding[3] * 0.75)
        .style('font-size', `${titleFontSize}px`)
    },
    drawBarEnter (enter) {
      const {
        rectFill,
        rectHighlightFill,
        labelFill,
        labelHighlightFill,
        labelFontSize
      } = this.barInfo
      let bar = enter
        .append('g')
        .classed('bar', true)
        .attr('transform', d => `translate(${this.xScale(0) + 1}, ${this.yScale(d.label)})`)
      bar
        .append('rect')
        .classed('bar-rect', true)
        .attr('width', d => this.xScale(d.population))
        .attr('height', this.yScale.bandwidth())
        .attr('fill', rectFill)
        .attr('transform', 'scale(0, 1)')
        .transition()
        .duration(400)
        .delay((d, index) => index * 400)
        .attr('transform', 'scale(1, 1)')
      bar
        .append('text')
        .classed('bar-label', true)
        .text(d => this.formatPopulation(d.population))
        .attr('text-anchor', 'start')
        .attr('x', d => this.xScale(d.population))
        .attr('y', this.yScale.bandwidth() / 2)
        .attr('dx', 0)
        .attr('dy', '0.35em')
        .attr('fill', labelFill)
        .attr('fill-opacity', 0)
        .style('font-size', labelFontSize)
        .transition()
        .delay((d, index) => index * 400 + 500)
        .duration(400)
        .attr('dx', 10)
        .attr('fill-opacity', 1)
      bar
        .selectAll('.bar-rect')
        .filter(d => d.beginAge < 40)
        .transition()
        .delay(4300)
        .duration(400)
        .attr('fill', rectHighlightFill)
      bar
        .selectAll('.bar-label')
        .filter(d => d.beginAge < 40)
        .transition()
        .delay(4300)
        .duration(400)
        .attr('fill', labelHighlightFill)
    },
    drawBarUpdate (update) {
      const {
        rectFill,
        rectHighlightFill,
        labelFill,
        labelHighlightFill,
        labelFontSize
      } = this.barInfo
      let bar = update
        .attr('transform', d => `translate(${this.xScale(0) + 1}, ${this.yScale(d.label)})`)
      bar
        .select('.bar-rect')
        .attr('width', d => this.xScale(d.population))
        .attr('height', this.yScale.bandwidth())
        .attr('fill', rectFill)
        .attr('transform', 'scale(1, 1)')
      bar
        .select('.bar-label')
        .text(d => this.formatPopulation(d.population))
        .attr('text-anchor', 'start')
        .attr('x', d => this.xScale(d.population))
        .attr('y', this.yScale.bandwidth() / 2)
        .attr('dx', 10)
        .attr('dy', '0.35em')
        .attr('fill', labelFill)
        .attr('fill-opacity', 1)
        .style('font-size', labelFontSize)
      bar
        .filter(d => d.beginAge < 40)
        .select('.bar-rect')
        .attr('fill', rectHighlightFill)
      bar
        .filter(d => d.beginAge < 40)
        .select('.bar-label')
        .attr('fill', labelHighlightFill)
    },
    drawBarExit (exit) {
      exit.remove()
    },
    formatPopulation (value) {
      let num = value
      let unit = '人'
      if (value >= 10000) {
        num = Math.floor(value / 10000)
        unit = '萬'
      }
      return `${num} ${unit}`
    }
  },
  mounted () {
    const svg = d3
      .select(this.$refs.figure)
      .append('svg')
      // .attr('viewBox', SVG_CONFIG.viewBox)
      // .attr('preserveAspectRatio', SVG_CONFIG.preserveAspectRatio)
      // .attr('width', SVG_CONFIG.width)
      // .attr('height', SVG_CONFIG.height)
    const svgChart = svg
      .append('g')
      .style('opacity', 0)
    const xScale = d3.scaleLinear()
    const yScale = d3.scaleBand()
    const xAxis = d3.axisBottom(xScale)
    const yAxis = d3.axisLeft(yScale)
    // add x-axis
    svgChart
      .append('g')
      .classed('x-axis', true)
    // add y-axis
    svgChart
      .append('g')
      .classed('y-axis', true)
    // add x-axis title
    svgChart
      .append('text')
      .classed('axis-title', true)
      .classed('x-axis-title', true)
      .text('人數（萬）')
      .attr('text-anchor', 'end')
      .attr('fill', '#949494')
    // add y-axis title
    svgChart
      .append('text')
      .classed('axis-title', true)
      .classed('y-axis-title', true)
      .text('年齡（歲）')
      .attr('text-anchor', 'end')
      .attr('fill', '#949494')
      .attr('transform', 'rotate(-90)')
      // .style('writing-mode', 'tb')
    this.svg = svg
    this.svgChart = svgChart
    this.xScale = xScale
    this.yScale = yScale
    this.xAxis = xAxis
    this.yAxis = yAxis
  }
}
</script>

<style lang="scss">
.population-chart {
  $comp: &;

  &__caption {
    font-size: 1.125em;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.1em;
    opacity: 0;
    @include rwd($RWD_TABLET_SMALL) {
      font-size: 1.25em;
    }
    @include rwd($RWD_TABLET) {
      font-size: 1.5em;
    }
  }

  &__figure {
    position: relative;
    max-height: 400px;
    margin: 0 auto;
    @include use-fixed-ratio(320, 240);
    @include rwd($RWD_TABLET_SMALL) {
      margin-top: 10px;
    }
    @include rwd($RWD_TABLET) {
      margin-top: 10px;
    }

    svg {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    text {
      font-family: "Montserrat", "Noto Sans TC", sans-serif;
    }
  }

  &#{$comp}--inview {
    #{$comp}__caption {
      opacity: 1;
      transition: opacity 0.4s;
    }
  }
}
</style>
